import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationService } from '../../../core/service/navigation.service';
import { Features, ModuleNames } from '../../model/account-definition';
import { UserAccountService } from '../user-account.service';

@Injectable()
export class InvoicingGuard {
  constructor(
    private _userAccount: UserAccountService,
    private _navigation: NavigationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    return this._userAccount.current.pipe(
      map(account => {
        const requiredWriteFeature = this.getRouteData(route, 'invoicingWriteFeature');
        const requiredReadFeature = this.getRouteData(route, 'invoicingReadFeature');
        const hasRequiredAccess = requiredWriteFeature
          ? account.hasAccess(ModuleNames.Invoicing, requiredWriteFeature)
          : requiredReadFeature
            ? account.hasAccess(ModuleNames.Invoicing, requiredReadFeature)
              || account.hasAccess(ModuleNames.Invoicing, this.getLegacyWriteFeature(requiredReadFeature))
            : account.hasAccess(ModuleNames.Invoicing);

        if (hasRequiredAccess) {
          return true;
        }

        this._navigation.navigatePageNotFound();
        return false;
      })
    );
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    return this.canActivate(childRoute, state);
  }

  private getRouteData(route: ActivatedRouteSnapshot, key: string): string | undefined {
    for (let current: ActivatedRouteSnapshot | null = route; current; current = current.parent) {
      const value = current.data?.[key];
      if (value) {
        return value;
      }
    }

    return undefined;
  }

  private getLegacyWriteFeature(readFeature: string): string | undefined {
    const legacyFeatures: Record<string, string> = {
      [Features.ShowInvoices]: Features.ManageInvoices,
      [Features.ShowPrintOrders]: Features.ManageInvoices,
      [Features.ShowIncorrectPayments]: Features.ManageIncorrectPayments,
      [Features.ShowBankTransactionImports]: Features.ManageIncorrectPayments,
      [Features.ShowContactBalanceTransactions]: Features.ManageIncorrectPayments,
      [Features.ShowInvoiceReminders]: Features.ManageInvoices,
      [Features.ShowAmortizationPlans]: Features.ManageInvoices,
      [Features.ShowAmortizationPlanPaymentNotices]: Features.ManageInvoices,
      [Features.ShowDebtCollectionCases]: Features.ManageDebtCollection,
      [Features.ShowInvoiceRowGroups]: Features.InvoiceRowGroup,
    };

    return legacyFeatures[readFeature];
  }
}
